import { candyTileProps } from './candy-tile.util';
import { AppIcon } from '@/components/ui/app-icon';
import { CopilotIcon } from '@/components/ui/copilot-icon.component';
import { DynamicIcon } from '@/components/ui/icons/dynamic-icon';
import { ScrollArea } from '@/components/ui/scroll-area';
import { requestChatHistoryRail } from '@/containers/copilot/chat-history-rail.util';
import { iconBoxArchive, iconPlus } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useMemo, type MouseEvent, type ReactNode } from 'react';
import { LockBadge, LockMark } from '../lock-mark.component';
import { matchesPath } from '../nav-match.util';
import type { LockedUpgradeClick } from '../use-locked-upgrade-click.hook';
import { ChatTabIcon } from './chat-tab-icon.component';
import { chatRowKey, workspaceAgentId, type ChatTab } from './chat-tabs.util';
import {
  pinnedChatList,
  railSections,
  selectedRailKey,
  type RailPage,
  type RailSection,
} from './page-rail.util';
import { FlyoutItem, FlyoutLabel, RailFlyout } from './rail-flyout.component';
import { RunningPulse } from './running-pulse.component';
import { RunningMark } from './sidebar-nav.component';
import { SidebarRule, SidebarTooltip } from './sidebar-tooltip.component';
import {
  RAIL_CELL,
  RAIL_GRID,
  RAIL_RULE,
  RAISED_CARD,
  SIDEBAR_HOVER,
  SIDEBAR_ICON,
} from './tabs-sidebar.styles';
import type { ChatTabsState } from './use-chat-tabs.hook';
import type { TabsNav } from './use-tabs-nav.hook';

const FOCUS_RING =
  'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/50';

/** A rail cell's icon, a step up from the 16px icons in its flyout. */
const RAIL_ICON_SIZE = 19;

/** A locked page's upgrade note, as the tabbed sidebar words it. */
const lockedNote = (page: RailPage) =>
  page.locked ? (page.lockedTooltip ?? 'Upgrade to unlock') : null;

/**
 * A page's click: for a locked page, the sidebar's locked click with the
 * page's id; undefined for anything else, which follows its link.
 */
function lockedClick(
  page: RailPage,
  onLockedClick: LockedUpgradeClick | undefined
) {
  if (!page.locked || !onLockedClick) return undefined;
  return (event: MouseEvent<HTMLElement>) => onLockedClick(page.pageId, event);
}

/**
 * The folded tabbed sidebar, between the command bar and What's new: Home
 * first, a cell for each section with its pages in a flyout, Reports, and
 * past a rule Clu, holding new and past chats and Chat history. One cell is
 * selected: Clu's while a chat is the page, else the cell holding the page
 * on screen.
 */
export function PageRail({ nav }: { nav: TabsNav }) {
  const { access, chats, layout, onLockedClick } = nav;
  const pathname = useLocation({ select: (location) => location.pathname });
  const sections = useMemo(() => railSections(access), [access]);
  const selectedKey = selectedRailKey(sections, pathname);
  const pinned = useMemo(
    () => pinnedChatList(layout.layout, chats.pinned),
    [chats.pinned, layout.layout]
  );
  // A chat is the page on /clu, and in an agent's workspace while one of the
  // user's chats is open there. In the Clu dock a chat sits beside the page,
  // which keeps the selection.
  const chatIsPage =
    chats.available &&
    (matchesPath(pathname, '/clu') ||
      (workspaceAgentId(pathname) !== null && chats.activeId !== null));
  const railKey = chatIsPage ? null : selectedKey;
  return (
    <ScrollArea className='-mx-2 min-h-0 flex-1 self-stretch'>
      <div className='flex flex-col items-center px-2 pt-2 pb-1'>
        <nav aria-label='Pages'>
          <ul className={RAIL_GRID}>
            {sections.map((section) => (
              <li key={section.id} className='grid'>
                {section.pages.length === 1 ? (
                  <RailLink
                    page={section.pages[0]}
                    selected={section.pages[0].key === railKey}
                    onLockedClick={onLockedClick}
                  />
                ) : (
                  <RailSectionCell
                    section={section}
                    selected={section.pages.some(
                      (page) => page.key === railKey
                    )}
                    selectedKey={selectedKey}
                    onLockedClick={onLockedClick}
                  />
                )}
              </li>
            ))}
          </ul>
        </nav>
        {chats.available && (
          <>
            <SidebarRule className={RAIL_RULE} />
            <RailClu chats={chats} pinned={pinned} selected={chatIsPage} />
          </>
        )}
      </div>
    </ScrollArea>
  );
}

/** A section of one page, such as Home: the page's own cell. */
function RailLink({
  page,
  selected,
  onLockedClick,
}: {
  page: RailPage;
  selected: boolean;
  onLockedClick?: LockedUpgradeClick;
}) {
  const note = lockedNote(page);
  const name = note ? `${page.label} · ${note}` : page.label;
  return (
    <SidebarTooltip label={name}>
      <Link
        to={page.href}
        activeOptions={{ exact: page.exact ?? false }}
        aria-label={name}
        aria-current={selected ? 'page' : undefined}
        data-selected={selected || undefined}
        onClick={lockedClick(page, onLockedClick)}
        {...candyTileProps(page.iconName, 8)}
        className={cn(
          'relative grid place-items-center text-foreground transition-colors',
          RAIL_CELL,
          FOCUS_RING,
          selected ? RAISED_CARD : SIDEBAR_HOVER
        )}
      >
        {page.locked && <LockBadge />}
        <DynamicIcon
          name={page.iconName}
          size={RAIL_ICON_SIZE}
          weight={selected ? 'fill' : undefined}
        />
      </Link>
    </SidebarTooltip>
  );
}

/** A section of several pages: one cell, its pages in a flyout. */
function RailSectionCell({
  section,
  selected,
  selectedKey,
  onLockedClick,
}: {
  section: RailSection;
  /** The cell is the rail's one selected cell. */
  selected: boolean;
  /** The page on screen, current in the flyout. */
  selectedKey: string | null;
  onLockedClick?: LockedUpgradeClick;
}) {
  return (
    <RailFlyout
      label={section.label}
      selected={selected}
      cellProps={candyTileProps(section.iconName, 8)}
      icon={
        <>
          {/* Every page locked, as Engage on Free: the cell carries the lock. */}
          {section.pages.every((page) => page.locked) && <LockBadge />}
          <DynamicIcon name={section.iconName} size={RAIL_ICON_SIZE} />
        </>
      }
    >
      {section.pages.map((page) => {
        const current = page.key === selectedKey;
        const note = lockedNote(page);
        return (
          <FlyoutItem key={page.key} current={current}>
            <Link
              to={page.href}
              activeOptions={{ exact: page.exact ?? false }}
              aria-label={note ? `${page.label} · ${note}` : undefined}
              onClick={lockedClick(page, onLockedClick)}
            >
              <ItemIcon muted>
                <DynamicIcon
                  name={page.iconName}
                  size={16}
                  weight={current ? 'fill' : undefined}
                />
              </ItemIcon>
              <span className='min-w-0 flex-1 truncate'>{page.label}</span>
              {page.locked && (
                // In a span: the menu item sizes the svgs right inside it.
                <span className='grid shrink-0 place-items-center'>
                  <LockMark />
                </span>
              )}
            </Link>
          </FlyoutItem>
        );
      })}
    </RailFlyout>
  );
}

/**
 * Clu's cell, under the pages: the bird, whose flyout has New chat, the
 * pinned chats, the chats the open sidebar shows as tabs, and Chat history.
 * The cell is selected while a chat is the page, and the bird pulses while
 * any chat's reply streams.
 */
function RailClu({
  chats,
  pinned,
  selected,
}: {
  chats: ChatTabsState;
  pinned: readonly ChatTab[];
  selected: boolean;
}) {
  const navigate = useNavigate();
  const running = chats.runningIds.size > 0;
  const icon = <CopilotIcon className='h-5 w-5' />;
  const item = (tab: ChatTab) => (
    <FlyoutItem key={chatRowKey(tab)} current={tab.id === chats.activeId}>
      <ChatTarget tab={tab} onOpenClu={chats.openCluChat}>
        <ItemIcon>
          <ChatTabIcon tab={tab} size={16} />
        </ItemIcon>
        <span className='min-w-0 flex-1 truncate'>{tab.title}</span>
        {chats.runningIds.has(tab.id) && <RunningMark />}
      </ChatTarget>
    </FlyoutItem>
  );
  return (
    <RailFlyout
      label='Clu'
      name={running ? 'Clu, running' : 'Clu'}
      selected={selected}
      icon={running ? <RunningPulse>{icon}</RunningPulse> : icon}
    >
      <FlyoutItem>
        <button type='button' onClick={chats.newChat}>
          <ItemIcon muted>
            <AppIcon icon={iconPlus} style={{ fontSize: 15 }} />
          </ItemIcon>
          New chat
        </button>
      </FlyoutItem>
      {pinned.length > 0 && <FlyoutLabel>Pinned</FlyoutLabel>}
      {pinned.map(item)}
      {chats.tabs.length > 0 && <FlyoutLabel>Recent</FlyoutLabel>}
      {chats.tabs.map(item)}
      <FlyoutItem>
        <button
          type='button'
          className='mt-1'
          onClick={() => {
            requestChatHistoryRail();
            void navigate({ to: '/clu' });
          }}
        >
          <ItemIcon muted>
            <AppIcon icon={iconBoxArchive} style={{ fontSize: 14 }} />
          </ItemIcon>
          Chat history
        </button>
      </FlyoutItem>
    </RailFlyout>
  );
}

/** The 16px column a flyout item's icon sits in. */
function ItemIcon({
  muted = false,
  children,
}: {
  /** The sidebar's grey icons; a chat's own icon keeps its colour. */
  muted?: boolean;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'grid w-4 shrink-0 place-items-center',
        muted && SIDEBAR_ICON
      )}
    >
      {children}
    </span>
  );
}

/**
 * What a chat opens: an agent's chat in the agent's workspace, a Clu chat
 * where openCluChat opens it. A chat still waiting for its id opens where
 * the runtime shows it.
 */
function ChatTarget({
  tab,
  onOpenClu,
  children,
  ...rest
}: {
  tab: ChatTab;
  onOpenClu: (id: string) => void;
  children: ReactNode;
  className?: string;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
  'aria-current'?: 'page';
}) {
  if (tab.agentId)
    return (
      <Link
        to='/agents/$agentId'
        params={{ agentId: tab.agentId }}
        search={tab.pending ? {} : { chat: tab.id }}
        // The chat, not the agent's workspace, is what the item opens.
        activeOptions={{ exact: true, includeSearch: true }}
        {...rest}
      >
        {children}
      </Link>
    );
  const { onClick, ...buttonProps } = rest;
  return (
    <button
      type='button'
      {...buttonProps}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onOpenClu(tab.id);
      }}
    >
      {children}
    </button>
  );
}
